import { useId, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { describeContractorFailure } from '../errors';

interface Props {
  current: number | null;
  /** EPN-252: the stored "approaching" percent (null = the default applies) and the percent in effect, for the placeholder. */
  currentPercent?: number | null;
  effectivePercent?: number | null;
  onClose: () => void;
  onSave: (value: number | null, percent: number | null) => Promise<void>;
}

/**
 * The company's 1099 reporting threshold. It has NO default and no value is assumed: leave it empty and no threshold warning is produced.
 * COMPLIANCE RULE CONFIRMATION REQUIRED: what the threshold should be is the company's decision, confirmed by a payroll/compliance reviewer.
 */
export function ThresholdDialog({ current, currentPercent = null, effectivePercent = null, onClose, onSave }: Props) {
  const uid = useId();
  const [value, setValue] = useState(current === null ? '' : String(current));
  const [percent, setPercent] = useState(currentPercent === null ? '' : String(currentPercent));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    const raw = value.trim();
    if (raw !== '' && (!/^\d{1,13}(\.\d{1,2})?$/.test(raw) || Number(raw) <= 0)) {
      setError('Enter an amount greater than zero with at most two decimals, or leave it empty for no threshold.');
      return;
    }
    const pct = percent.trim();
    if (pct !== '' && (!/^\d{1,2}$/.test(pct) || Number(pct) < 1 || Number(pct) > 99)) {
      setError('Enter a whole number from 1 to 99 for the approaching alert, or leave it empty for the default.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(raw === '' ? null : Number(raw), pct === '' ? null : Number(pct));
    } catch (err) {
      setError(describeContractorFailure(err, 'The threshold couldn’t be saved. Try again.').message);
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="1099 reporting threshold"
      size="sm"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <p className="muted">
        The readiness check warns when a contractor’s approved payments this calendar year, including this batch, reach this amount. Leave it empty to
        turn the warning off. Your company decides this value; EPN does not assume one.
      </p>
      <div className="jr-field">
        <label htmlFor={`${uid}-threshold`}>Threshold amount</label>
        <input id={`${uid}-threshold`} data-autofocus inputMode="decimal" value={value} placeholder="No threshold" aria-invalid={error ? true : undefined} onChange={(e) => setValue(e.target.value)} />
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-percent`}>Approaching alert (% of threshold)</label>
        <input id={`${uid}-percent`} inputMode="numeric" value={percent} placeholder={effectivePercent === null ? 'Default' : `Default (${effectivePercent}%)`} onChange={(e) => setPercent(e.target.value)} />
        <span className="muted">Contractor Payments flags a contractor as approaching the threshold from this share of it. Leave empty for the default.</span>
      </div>
      {error && <p className="jr-error" role="alert">{error}</p>}
    </ModalDialog>
  );
}
